import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Check } from 'lucide-react';

// ─── Shared logbook form types + renderer ────────────────────────────────────
// Extracted from Tasks.tsx (LogbookFormFieldInputs was page-private there) so the same
// field-rendering component serves three callers: the WO Checklist tab (the real fill flow),
// the Add/Edit WO modal's inline template preview, and the new Settings > Development JSON
// injector's live preview. One renderer, not a second one per caller.
//
// Per Chris's LB1 simplification (LOGBOOK_SYSTEM_DESIGN.md): one WO occurrence = one fill of a
// log, never a multi-day/month grid in a single fill. So `grid` mode here means "a static list
// of named rows filled once" (e.g. 32 fire extinguishers checked on one inspection visit), NOT
// a calendar-day or repeating week/month grid — no calendar_day row generation, no column_group
// repeat mechanics, no orientation/items_as_columns. Those are explicitly out of scope.

export interface LogbookFormField {
  key: string; label: string;
  type: 'text' | 'number' | 'date' | 'textarea' | 'select' | 'checkbox';
  options?: string[]; required?: boolean;
  // Admin/org_admin-authored range limits on number fields (FormSchemaBuilder's showRangeLimits).
  // Checked server-side on submission — see logbook-filled-entries.ts's findRangeViolations.
  min?: number; max?: number;
  // Groups this field under one of schema.sections' entries (matching LogbookGridRow's own
  // section_id below) — e.g. Daily Water Temperatures' 18 flat fields become "West Long Hallway"
  // (3 fields), "West Short Hallway" (3 fields), etc. instead of one undifferentiated grid.
  // Undefined = unsectioned, rendered in its own unlabeled block ahead of any real sections.
  section_id?: string;
}

export interface LogbookGridRow {
  row_id: string;
  label: string;
  section_id?: string;
  // Static per-row metadata (e.g. a fire extinguisher's Serial/Size) — display-only context next
  // to the row label, never fillable/editable at fill time (that's what row_fields are for).
  meta?: Record<string, string>;
}

export interface LogbookFormSchema {
  form_mode: 'single_form' | 'grid';
  instructions?: string;
  sections?: { section_id: string; label: string }[];
  // single_form mode — a flat list of field:value readings (what already existed before this
  // shape had a name; this is the only mode the visual FormSchemaBuilder-based UI authors).
  fixed_fields?: LogbookFormField[];
  // grid mode — a static/manual list of named rows, each filled once against the same set of
  // row_fields (e.g. Pressure/Pin/Hose checked on every extinguisher). Authored via the JSON
  // injector, not the visual builder.
  rows?: LogbookGridRow[];
  row_fields?: LogbookFormField[];
}

// Grid row values are stored flat in the same filled_data object as single_form values, keyed
// as `${row_id}__${field_key}` — no nested structure, so save/load/range-check code doesn't need
// a second data shape for grid mode.
export function gridFieldKey(rowId: string, fieldKey: string): string {
  return `${rowId}__${fieldKey}`;
}

// Client-side mirror of logbook-filled-entries.ts's checkFieldRange — used only for real-time
// out-of-range feedback (shake/red state, auto-checking the follow-up-ticket box, and the
// pre-save confirmation list). Server-side findRangeViolations remains the authoritative check on
// save; this never blocks or rejects a value, only surfaces it before the user hits Save.
function checkLogbookFieldRange(field: LogbookFormField, raw: unknown): number | null {
  if (field.type !== 'number' || (field.min === undefined && field.max === undefined)) return null;
  if (raw === undefined || raw === null || raw === '') return null;
  const value = typeof raw === 'number' ? raw : Number(raw);
  if (Number.isNaN(value)) return null;
  if ((field.min !== undefined && value < field.min) || (field.max !== undefined && value > field.max)) return value;
  return null;
}

// Number input for a range-limited field — shakes once (reusing buttonAnimations.css's shared
// `dangerShake` keyframes, already loaded globally by every page that renders this component --
// see BUTTON_SYSTEM.md section 7) the moment a value first goes out of range, then stays
// red-tinted (border + background, both derived from var(--accent-danger) -- the app's existing
// error color, never a new hardcoded hex) for as long as it remains out of range. Clears itself
// automatically (effect re-runs, style falls away) once the value is corrected or emptied. Plain
// non-ranged number fields render identically to a normal input -- this only activates once a
// field actually has min/max configured.
function NumberRangeInput({ field, value, onChange, disabled, style }: {
  field: LogbookFormField; value: string; onChange: (v: string) => void; disabled?: boolean; style?: CSSProperties;
}) {
  const outOfRange = checkLogbookFieldRange(field, value) !== null;
  const [shake, setShake] = useState(false);
  const wasOutOfRange = useRef(false);

  // Fires only on the false->true transition (dependency is the boolean, not the raw value) so
  // the shake plays once on entering an invalid state, not on every keystroke while it stays
  // invalid -- the persistent red border/background (below) is what signals "still out of range".
  useEffect(() => {
    if (outOfRange && !wasOutOfRange.current) {
      setShake(true);
      const t = setTimeout(() => setShake(false), 300);
      wasOutOfRange.current = true;
      return () => clearTimeout(t);
    }
    if (!outOfRange) wasOutOfRange.current = false;
  }, [outOfRange]);

  return (
    <input
      type="number" className="form-input" disabled={disabled}
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        ...style,
        ...(outOfRange ? {
          borderColor: 'var(--accent-danger)',
          background: 'rgba(239,68,68,0.08)',
          animation: shake ? 'dangerShake 0.3s ease' : undefined,
        } : {}),
      }}
    />
  );
}

// Shared field renderer. `checklistStyle` gives checkbox fields the polished check-item
// treatment (Checklist tab); `compact` renders every field as a small inline label+input pair
// instead of a stacked full-width block, for grid-mode rows where each row needs 2-4 short
// fields side by side rather than stacked (32+ rows stacked 3 fields each would be unusably tall).
export function LogbookFormFieldInputs({ fields, values, onChange, disabled, checklistStyle, compact }: {
  fields: LogbookFormField[];
  values: Record<string, string | boolean>;
  onChange: (key: string, value: string | boolean) => void;
  disabled?: boolean;
  checklistStyle?: boolean;
  compact?: boolean;
}) {
  function renderField(field: LogbookFormField) {
    const input = field.type === 'textarea' ? (
      <textarea
        className="form-input" rows={compact ? 1 : 2} disabled={disabled}
        value={(values[field.key] as string) ?? ''}
        onChange={e => onChange(field.key, e.target.value)}
        style={compact ? { width: 140, fontSize: 12, padding: '4px 8px' } : undefined}
      />
    ) : field.type === 'select' ? (
      <select
        className="form-input" disabled={disabled}
        value={(values[field.key] as string) ?? ''}
        onChange={e => onChange(field.key, e.target.value)}
        style={compact ? { width: 110, fontSize: 12, padding: '4px 6px' } : undefined}
      >
        <option value="">— Select —</option>
        {(field.options ?? []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : field.type === 'checkbox' ? (
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: disabled ? 'default' : 'pointer', fontSize: compact ? 12 : 13 }}>
        <input
          type="checkbox" disabled={disabled}
          checked={(values[field.key] as boolean) ?? false}
          onChange={e => onChange(field.key, e.target.checked)}
        />
        {compact ? null : field.label}
      </label>
    ) : field.type === 'number' ? (
      <NumberRangeInput
        field={field} disabled={disabled}
        value={(values[field.key] as string) ?? ''}
        onChange={v => onChange(field.key, v)}
        style={compact ? { width: 80, fontSize: 12, padding: '4px 8px' } : undefined}
      />
    ) : (
      <input
        type={field.type === 'date' ? 'date' : 'text'}
        className="form-input" disabled={disabled}
        value={(values[field.key] as string) ?? ''}
        onChange={e => onChange(field.key, e.target.value)}
        style={compact ? { width: 110, fontSize: 12, padding: '4px 8px' } : undefined}
      />
    );

    if (compact) {
      // Checkbox fields already render their own label inline; every other type gets a small
      // label above a narrow input.
      if (field.type === 'checkbox') {
        return <div key={field.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{field.label}</span>
          {input}
        </div>;
      }
      return (
        <div key={field.key}>
          <label style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 2 }}>
            {field.label}{field.required && ' *'}
          </label>
          {input}
        </div>
      );
    }

    // Textareas span the full grid width -- a free-text notes field cramped into a ~220px
    // column reads worse than the wasted-whitespace problem the grid is fixing in the first
    // place. Every other field type stays a normal grid cell.
    return (
      <div key={field.key} style={field.type === 'textarea' ? { gridColumn: '1 / -1' } : undefined}>
        <label style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>
          {field.label}{field.required && ' *'}
        </label>
        {input}
      </div>
    );
  }

  if (compact) {
    return <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>{fields.map(renderField)}</div>;
  }

  // Responsive grid, not a single stacked column -- auto-fill/minmax fits as many ~220px+
  // columns as the panel allows (3+ on a typical WO Checklist/Add WO panel width, more on wide
  // viewports) and collapses to fewer columns on narrower ones with no separate breakpoint logic
  // needed. Shared by every LogbookFormRenderer caller (Checklist tab, Add WO modal preview,
  // Template Builder preview) since they all render through this one function.
  if (!checklistStyle) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
        {fields.map(renderField)}
      </div>
    );
  }

  // checklistStyle: checkbox fields become polished check-items (grouped first), every other
  // field type stays a normal labeled input (grouped after) — same field set, just regrouped.
  const checkboxFields = fields.filter(f => f.type === 'checkbox');
  const otherFields = fields.filter(f => f.type !== 'checkbox');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <style>{`
        .wo-check { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 10px; border: 1px solid var(--border-subtle); background: rgba(255,255,255,0.02); transition: all 0.14s ease; cursor: pointer; }
        .wo-check:hover { border-color: var(--border-default); background: rgba(255,255,255,0.045); }
        .wo-check-box { width: 22px; height: 22px; border-radius: 6px; border: 2px solid var(--border-strong); flex-shrink: 0; display: flex; align-items: center; justify-content: center; transition: background 0.15s ease, border-color 0.15s ease; color: #fff; }
        .wo-check-box svg { opacity: 0; transform: scale(0.5); transition: opacity 0.15s ease, transform 0.2s cubic-bezier(0.34,1.56,0.64,1); }
        .wo-check-done .wo-check-box { background: var(--accent-success); border-color: var(--accent-success); }
        .wo-check-done .wo-check-box svg { opacity: 1; transform: scale(1); }
        .wo-check-text { font-weight: 500; font-size: 0.9rem; color: var(--text-primary); transition: color 0.15s ease; }
        .wo-check-done .wo-check-text { text-decoration: line-through; color: var(--text-muted); }
      `}</style>
      {checkboxFields.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {checkboxFields.map(field => {
            const done = !!values[field.key];
            return (
              <div
                key={field.key}
                className={`wo-check${done ? ' wo-check-done' : ''}`}
                onClick={() => !disabled && onChange(field.key, !done)}
              >
                <span className="wo-check-box"><Check size={13} strokeWidth={3} /></span>
                <span className="wo-check-text">{field.label}</span>
              </div>
            );
          })}
        </div>
      )}
      {otherFields.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
          {otherFields.map(renderField)}
        </div>
      )}
    </div>
  );
}

// Top-level renderer — takes a full LogbookFormSchema and branches single_form vs grid. This is
// the component every caller (Checklist tab, Add WO modal preview, JSON injector preview) should
// use instead of reaching into fixed_fields/rows/row_fields directly.
export function LogbookFormRenderer({ schema, values, onChange, disabled, checklistStyle }: {
  schema: LogbookFormSchema;
  values: Record<string, string | boolean>;
  onChange: (key: string, value: string | boolean) => void;
  disabled?: boolean;
  checklistStyle?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {schema.instructions && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)', whiteSpace: 'pre-wrap' }}>
          {schema.instructions}
        </div>
      )}

      {schema.form_mode === 'single_form' ? (
        <SingleFormBody schema={schema} values={values} onChange={onChange} disabled={disabled} checklistStyle={checklistStyle} />
      ) : (
        <GridFormBody schema={schema} values={values} onChange={onChange} disabled={disabled} />
      )}
    </div>
  );
}

// Groups fixed_fields by section_id (same section-card treatment GridFormBody already uses for
// grid-mode rows below) instead of one flat grid mixing every field together regardless of
// logical grouping. No sections defined at all -> unchanged single flat grid (the common case for
// simple templates), so this is a pure addition, not a behavior change for existing schemas.
function SingleFormBody({ schema, values, onChange, disabled, checklistStyle }: {
  schema: LogbookFormSchema;
  values: Record<string, string | boolean>;
  onChange: (key: string, value: string | boolean) => void;
  disabled?: boolean;
  checklistStyle?: boolean;
}) {
  const fields = schema.fixed_fields ?? [];
  const sections = schema.sections ?? [];

  if (sections.length === 0) {
    return <LogbookFormFieldInputs fields={fields} values={values} onChange={onChange} disabled={disabled} checklistStyle={checklistStyle} />;
  }

  // Group into contiguous runs of the same section_id, preserving fixed_fields' own order --
  // NOT "every unsectioned field first, then every section" (that would yank a lone unsectioned
  // field like Daily Water Temperatures' "West Wing Air Temperature", which sits between two
  // sections in the original authoring order, up to the top of the form). A run with no
  // section_id renders as a bare block; a run with one renders as a labeled card.
  const labelBySection = new Map(sections.map(s => [s.section_id, s.label]));
  const segments: { sectionId?: string; fields: LogbookFormField[] }[] = [];
  for (const f of fields) {
    const last = segments[segments.length - 1];
    if (last && last.sectionId === f.section_id) last.fields.push(f);
    else segments.push({ sectionId: f.section_id, fields: [f] });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {segments.map((seg, i) => seg.sectionId ? (
        <div key={i} style={{ border: '1px solid var(--border-subtle)', borderRadius: 10, padding: 14, background: 'rgba(255,255,255,0.02)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid var(--border-subtle)' }}>
            {labelBySection.get(seg.sectionId) ?? 'Section'}
          </div>
          <LogbookFormFieldInputs fields={seg.fields} values={values} onChange={onChange} disabled={disabled} checklistStyle={checklistStyle} />
        </div>
      ) : (
        <LogbookFormFieldInputs key={i} fields={seg.fields} values={values} onChange={onChange} disabled={disabled} checklistStyle={checklistStyle} />
      ))}
    </div>
  );
}

function GridFormBody({ schema, values, onChange, disabled }: {
  schema: LogbookFormSchema;
  values: Record<string, string | boolean>;
  onChange: (key: string, value: string | boolean) => void;
  disabled?: boolean;
}) {
  const rows = schema.rows ?? [];
  const rowFields = schema.row_fields ?? [];
  const sections = schema.sections ?? [];

  function renderRow(row: LogbookGridRow) {
    const remapped = rowFields.map(f => ({ ...f, key: gridFieldKey(row.row_id, f.key) }));
    return (
      <div key={row.row_id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.02)' }}>
        <div style={{ flex: '0 0 auto', minWidth: 140 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{row.label}</div>
          {row.meta && Object.keys(row.meta).length > 0 && (
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
              {Object.entries(row.meta).map(([k, v]) => `${k}: ${v}`).join(' · ')}
            </div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <LogbookFormFieldInputs
            fields={remapped}
            values={values}
            onChange={onChange}
            disabled={disabled}
            compact
          />
        </div>
      </div>
    );
  }

  if (sections.length === 0) {
    return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{rows.map(renderRow)}</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {sections.map(section => {
        const sectionRows = rows.filter(r => r.section_id === section.section_id);
        if (sectionRows.length === 0) return null;
        return (
          <div key={section.section_id}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>{section.label}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{sectionRows.map(renderRow)}</div>
          </div>
        );
      })}
      {(() => {
        const unsectioned = rows.filter(r => !r.section_id);
        if (unsectioned.length === 0) return null;
        return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{unsectioned.map(renderRow)}</div>;
      })()}
    </div>
  );
}

// Progress/count helpers shared by the Checklist tab's progress meter and tab badge — grid mode
// counts checkbox completion across every row instance, not just once.
export function countLogbookCheckboxProgress(schema: LogbookFormSchema | null | undefined, values: Record<string, string | boolean>): { done: number; total: number } {
  if (!schema) return { done: 0, total: 0 };
  if (schema.form_mode === 'single_form') {
    const checkboxFields = (schema.fixed_fields ?? []).filter(f => f.type === 'checkbox');
    return { done: checkboxFields.filter(f => !!values[f.key]).length, total: checkboxFields.length };
  }
  const checkboxRowFields = (schema.row_fields ?? []).filter(f => f.type === 'checkbox');
  const rows = schema.rows ?? [];
  let done = 0;
  const total = checkboxRowFields.length * rows.length;
  for (const row of rows) {
    for (const f of checkboxRowFields) {
      if (values[gridFieldKey(row.row_id, f.key)]) done++;
    }
  }
  return { done, total };
}

// ─── Real-time out-of-range violation detection ──────────────────────────────────────────────
// Client-side mirror of logbook-filled-entries.ts's findRangeViolations — used to auto-check the
// "auto-create a follow-up ticket" box and build the pre-save confirmation dialog's field list.
// Never authoritative (the server re-runs its own findRangeViolations on save) and never blocks
// saving on its own.
export interface LogbookRangeViolation {
  field: LogbookFormField;
  value: number;
  direction: 'below' | 'above';
  // Best-effort room/location context: the grid row's own label in grid mode, or -- in
  // single_form mode -- the current value of a sibling select field sharing the same section
  // (e.g. Daily Water Temperatures' room-number dropdown sits in the same section as its water/air
  // temperature fields), falling back to the section's own label if no select value is chosen yet.
  location?: string;
}

export function findLogbookRangeViolations(schema: LogbookFormSchema | null | undefined, values: Record<string, string | boolean>): LogbookRangeViolation[] {
  if (!schema) return [];
  const violations: LogbookRangeViolation[] = [];

  function pushIfViolating(field: LogbookFormField, raw: unknown, location: string | undefined) {
    const value = checkLogbookFieldRange(field, raw);
    if (value === null) return;
    const direction: 'below' | 'above' = field.min !== undefined && value < field.min ? 'below' : 'above';
    violations.push({ field, value, direction, location });
  }

  if (schema.form_mode === 'single_form') {
    const fields = schema.fixed_fields ?? [];
    const sections = schema.sections ?? [];
    const labelBySection = new Map(sections.map(s => [s.section_id, s.label]));
    // Same contiguous-run grouping SingleFormBody uses to render sections, so a violation's
    // "location" can borrow the value of a sibling select field in the same run (its room/unit
    // picker) instead of falling back to just the generic section label.
    const segments: { sectionId?: string; fields: LogbookFormField[] }[] = [];
    for (const f of fields) {
      const last = segments[segments.length - 1];
      if (last && last.sectionId === f.section_id) last.fields.push(f);
      else segments.push({ sectionId: f.section_id, fields: [f] });
    }
    for (const seg of segments) {
      const locationField = seg.fields.find(f => f.type === 'select' && values[f.key]);
      const location = (locationField ? String(values[locationField.key]) : undefined)
        ?? (seg.sectionId ? labelBySection.get(seg.sectionId) : undefined);
      for (const field of seg.fields) pushIfViolating(field, values[field.key], location);
    }
    return violations;
  }

  for (const row of schema.rows ?? []) {
    for (const field of schema.row_fields ?? []) {
      pushIfViolating(field, values[gridFieldKey(row.row_id, field.key)], row.label);
    }
  }
  return violations;
}

// "Room 105 water temperature is below normal range" -- location + field label (lowercased) +
// direction. Falls back to just the field label when no location context was found.
export function formatLogbookViolationMessage(v: LogbookRangeViolation): string {
  const prefix = v.location ? `${v.location} ` : '';
  return `${prefix}${v.field.label.toLowerCase()} is ${v.direction} normal range`;
}
